/**
 * IANA timezone helpers shared by the form engine and the lodgement services. They lean on
 * `Intl` only, so the same code runs in the browser and on the server.
 */

/**
 * The shape of an IANA zone name (`Australia/Sydney`, `UTC`, `Etc/GMT+10`). `Intl` also accepts
 * bare offsets such as `+23:59`, which name no calendar anyone lives on and would let a caller
 * place `today` up to 36 hours from the host's; those are refused before `Intl` sees them.
 */
const IANA_NAME_SHAPE = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/;

/** Whether the string names a zone `Intl` knows (`Australia/Sydney`, `UTC`, `Etc/GMT+10`…). */
export function isValidIanaTimeZone(timeZone: string): boolean {
    if (timeZone.length > 64 || !IANA_NAME_SHAPE.test(timeZone)) return false;
    try {
        new Intl.DateTimeFormat('en-AU', { timeZone });
        return true;
    } catch {
        return false;
    }
}

const pad = (value: number | string) => String(value).padStart(2, '0');

/**
 * The calendar day (`YYYY-MM-DD`) of an instant in a zone. No zone means the runtime's own
 * local zone: the respondent's browser, or a server that must not guess. An unknown zone is a
 * caller error and throws, so the boundary that accepted it is the one that has to validate.
 */
export function calendarDayIn(instant: Date, timeZone?: string): string {
    if (timeZone === undefined) {
        return `${String(instant.getFullYear())}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`;
    }
    // `en-CA` formats as `YYYY-MM-DD`; the parts are read back rather than trusted as a string.
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((candidate) => candidate.type === type)?.value;
    const [year, month, day] = [part('year'), part('month'), part('day')];
    if (!year || !month || !day) throw new RangeError(`Could not resolve a calendar day in ${timeZone}`);
    return `${year.padStart(4, '0')}-${pad(month)}-${pad(day)}`;
}
